import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { publicIdSelector, userSelector } from '_app/features/session';
import * as propertyApi from '_app/features/property/shared/propertyApi';
import { propertyQueryKeys } from '_app/features/property/shared/propertyQueryKeys';
import { usePropertyInteractions } from '_app/features/property/shared/usePropertyInteractions';
import Utils from '_app/utils';

export const usePropertyDetailController = (propertyId) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const publicId = useSelector(publicIdSelector);
  const user = useSelector(userSelector);

  const [chatVisible, setChatVisible] = useState(false);
  const [showActivities, setShowActivities] = useState(false);
  const { chatInteractions, propertyActions } = usePropertyInteractions({
    user,
    onPreApprovalSuccess: () => {
      setChatVisible(true);
    },
  });

  const propertyQuery = useQuery({
    queryKey: propertyQueryKeys.detail(propertyId),
    queryFn: () => propertyApi.fetchProperty(propertyId),
    enabled: !!publicId && !!propertyId,
  });

  const chatQuery = useQuery({
    queryKey: propertyQueryKeys.chat(propertyId),
    queryFn: () => propertyApi.fetchChat(propertyId),
    enabled: !!publicId && !!propertyId,
  });

  const recentActivitiesQuery = useQuery({
    queryKey: propertyQueryKeys.recentActivities(propertyId),
    queryFn: () => propertyApi.fetchRecentActivities(propertyId),
    enabled: !!publicId && !!propertyId && showActivities,
  });

  const upcomingQuery = useQuery({
    queryKey: propertyQueryKeys.openHouses(propertyId),
    queryFn: () => propertyApi.fetchUpcomingOpenHouses(propertyId),
    enabled: !!publicId && !!propertyId,
  });

  useEffect(() => {
    if (!propertyQuery.error) {
      return;
    }

    const errorMessage = Utils.getError(propertyQuery.error).message;

    if (errorMessage.includes('not found')) {
      Utils.showErrorToast('This property is no longer available.');
    } else {
      Utils.showErrorToast(errorMessage);
    }

    history.replace(`/${publicId}/listing/new/`);
  }, [history, propertyQuery.error, publicId]);

  const reserveOpenHouseMutation = useMutation({
    mutationFn: propertyApi.reserveOpenHouse,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: propertyQueryKeys.openHouses(propertyId) });
      Utils.showInfoToast('Successfully sent a reserve request.');
    },
    onError: (error) => {
      Utils.showErrorToast(Utils.getError(error).message);
    },
  });

  return {
    publicId,
    user,
    property: propertyQuery.data || null,
    loading: propertyQuery.isLoading,
    upcomingEvents: upcomingQuery.data || [],
    chat: {
      items: chatQuery.data || [],
      loading: chatQuery.isLoading || chatInteractions.loading,
      chatVisible,
      leadOwner: user?.lead_owner,
      userName: user?.name,
      onToggle: (value) => {
        if (value === 'open') {
          setChatVisible(true);
          return;
        }

        if (value === 'close') {
          setChatVisible(false);
          return;
        }

        setChatVisible((currentValue) => !currentValue);
      },
      onSend: (message) => chatInteractions.onSend(message, propertyId),
    },
    activities: {
      visible: showActivities,
      items: recentActivitiesQuery.data || [],
      loading: recentActivitiesQuery.isLoading,
      onOpen: () => setShowActivities(true),
      onClose: () => setShowActivities(false),
    },
    propertyActions,
    onReserveOpenHouse: (openHouse) => reserveOpenHouseMutation.mutate(openHouse.id),
  };
};
